import React, { useState, useEffect } from "react";
import {
    Form,
    Modal,
    TextArea,
    Segment,
    Button,
    Dropdown,
    Checkbox,
    Divider,
} from "semantic-ui-react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import ClubReview from "../../../models/ClubReview";
import Header from "../../../assets/components/header";
import Footer from "../../../assets/components/footer";
import LoadingPlaceholder from "../../../assets/components/LoadingPlaceholder";
import StarSelectDisplay from "../../../assets/components/StarSelectDisplay";
import MemberChip from "../../../assets/components/MemberChip";

export default function ({ history, match }) {
    const [review, setReview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [rejectionReason, setRejectionReason] = useState("DUPLICATE");
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);

    const rejectionReasons = [
        {
            key: 0,
            text: "User has reviewed this club / course too recently",
            value: "DUPLICATE",
        },
        {
            key: 1,
            text: "User is farming credit",
            value: "FARMING",
        },
        {
            key: 2,
            text: "Contains inappropriate or harmful content",
            value: "HARMFUL",
        },
        {
            key: 3,
            text: "Contains spam",
            value: "SPAM",
        },
        {
            key: 4,
            text: "Contains poor quality or unreadable content",
            value: "POOR_QUALITY",
        },
    ];

    useEffect(() => {
        const loadReview = async () => {
            try {
                const response = await ClubReview.show(match.params.id);
                setReview(response);
            } catch (_) {
                setError(true);
            }
            setLoading(false);
        };
        loadReview();
    }, []);

    const approve = async () => {
        setShowApproveModal(false);
        setLoading(true);
        try {
            await review.approve();
            review.approvedAt = new Date();
            review.status = "APPROVED";
            setReview(new ClubReview(review));
            setShowApproveModal(false);
        } catch (_) {
            setError(true);
        }
        setLoading(false);
    };

    const reject = async () => {
        setShowRejectModal(false);
        setLoading(true);
        try {
            await review.reject({ reason: rejectionReason });
            history.goBack();
        } catch (_) {
            setError(true);
            setLoading(false);
        }
    };

    const actions = () => {
        if (review.status === "APPROVED") {
            return (
                <p>
                    This review was approved on the{" "}
                    {format(review.approvedAt, "do 'of' LLLL yyyy")}
                </p>
            );
        }
        if (review.status === "REJECTED") {
            return (
                <p>
                    This review was rejected on the{" "}
                    {format(review.rejectedAt, "do 'of' LLLL yyyy")}:{" "}
                    {review.rejectedReason}
                </p>
            );
        }

        if (review.status === "EXPIRED") {
            return (
                <p>
                    This review expired on the{" "}
                    {format(review.expiredAt, "do 'of' LLLL yyyy")}
                </p>
            );
        }

        return (
            <>
                <Button
                    primary
                    className='theme mt-4'
                    onClick={() => setShowApproveModal(true)}
                >
                    Approve review
                </Button>
                <Button
                    primary
                    className='red'
                    onClick={() => setShowRejectModal(true)}
                >
                    Reject review
                </Button>
            </>
        );
    };

    const bookingReview = () => {
        return review.bookingReview ? (
            <Link to={`/reviews/bookings/${review.bookingReview._id}`}>
                View
            </Link>
        ) : (
            "N/A"
        );
    };

    const content = () => {
        if (loading === true) {
            return <LoadingPlaceholder fluid />;
        }

        if (error === true) {
            return <h2>Something went wrong.</h2>;
        }

        return (
            <>
                <div className='row skinny'>
                    <div className='col'>
                        <h3>Review Details</h3>
                        <div className='ml-3'>
                            <h4>
                                Club Name:{" "}
                                <Link to={`/golf-clubs/${review.club._id}`}>
                                    {review.club.name}
                                </Link>
                            </h4>
                            {review.course?.name && (
                                <h4>
                                    Course Name: {review.course?.name ?? "-"}
                                </h4>
                            )}
                            {review.bookingReview && (
                                <h4>
                                    Associated Booking review: {bookingReview()}
                                </h4>
                            )}
                        </div>
                        <Divider />
                        <h3>Member Details</h3>
                        <div className='ml-3'>
                            <MemberChip member={review.user} />
                            <p className='mt-3 mb-1'>
                                <b>Last review: </b>
                                {review.recentReviews.lastReview
                                    ? format(
                                          new Date(
                                              review.recentReviews.lastReview,
                                          ),
                                          "do 'of' LLLL yyyy",
                                      )
                                    : "None"}
                            </p>
                            <p className='mb-1'>
                                <b>Last review of {review.club.name}: </b>
                                {review.recentReviews.lastReviewOfClub
                                    ? format(
                                          new Date(
                                              review.recentReviews.lastReviewOfClub,
                                          ),
                                          "do 'of' LLLL yyyy",
                                      )
                                    : "None"}
                            </p>
                            <p className='mb-1'>
                                <b>No. of reviews in the past six months: </b>
                                {review.recentReviews.recentReviews}
                            </p>
                            <p className='mb-1'>
                                <b>No. of reviews in total: </b>
                                {review.recentReviews.totalReviews}
                            </p>
                        </div>
                    </div>
                </div>
                <div className='row skinny mt-3'>
                    <div className='col'>
                        <Divider />
                        <h3>Review Details</h3>
                        <Form>
                            <div className='ml-3'>
                                <Segment vertical>
                                    <h4>Mashie Member rating</h4>
                                    <StarSelectDisplay score={review.rating} />
                                </Segment>
                                {review.questions.map((question) => (
                                    <Segment vertical key={question._id}>
                                        <h4>{question.description}</h4>
                                        {question.questionType === "RATING" && (
                                            <StarSelectDisplay
                                                score={question.answer.rating}
                                            />
                                        )}
                                        {question.questionType === "TEXT" && (
                                            <TextArea
                                                value={question.answer.text}
                                                disabled
                                            />
                                        )}
                                    </Segment>
                                ))}
                                <Segment vertical>
                                    <h4>Review text</h4>
                                    <TextArea
                                        disabled
                                        placeholder='No text was provided by the member'
                                        value={review.reviewText}
                                    />
                                </Segment>
                            </div>
                            <Divider />
                            <h3>Review Processing</h3>
                            <div className='ml-3'>
                                <Segment vertical>
                                    <h4>Show review text</h4>
                                    <p>
                                        When turned off, the text associated
                                        with this review will not be visible to
                                        members in the app.
                                    </p>
                                    <Checkbox
                                        toggle
                                        checked={review.showText}
                                        disabled={
                                            review.approvedAt instanceof Date
                                        }
                                        onChange={() =>
                                            setReview(
                                                new ClubReview({
                                                    ...review,
                                                    showText:
                                                        review.showText ===
                                                        false,
                                                }),
                                            )
                                        }
                                    />
                                </Segment>

                                <Segment vertical>{actions()}</Segment>
                            </div>
                        </Form>
                    </div>
                </div>
                <Modal open={showApproveModal}>
                    <Modal.Header>Approve review</Modal.Header>
                    <Modal.Content>
                        <p>
                            Are you sure you want to approve this review? Once
                            approved, this review will be visible to other
                            members within the app.
                        </p>
                    </Modal.Content>
                    <Modal.Actions>
                        <Button
                            primary
                            className='theme'
                            content='Approve'
                            onClick={() => approve()}
                        />
                        <Button
                            content='Cancel'
                            onClick={() => setShowApproveModal(false)}
                        />
                    </Modal.Actions>
                </Modal>
                <Modal open={showRejectModal}>
                    <Modal.Header>Reject review</Modal.Header>
                    <Modal.Content style={{ height: "300px" }}>
                        <p>For what reason are you rejecting this review?</p>
                        <Dropdown
                            selection
                            placeholder={"Status"}
                            options={rejectionReasons}
                            value={rejectionReason}
                            onChange={(_, { value }) =>
                                setRejectionReason(value)
                            }
                        />
                    </Modal.Content>
                    <Modal.Actions>
                        <Button
                            primary
                            className='theme'
                            content='Reject'
                            onClick={() => reject()}
                        />
                        <Button
                            content='Cancel'
                            onClick={() => setShowRejectModal(false)}
                        />
                    </Modal.Actions>
                </Modal>
            </>
        );
    };

    return (
        <>
            <div className='d-flex main'>
                <Header history={history} />
                <div className='container'>{content()}</div>
            </div>
            <Footer />
        </>
    );
}
